/**
 * Artifacts, noise & signal quality — v10.3.0
 *
 * Convierte `signal_quality` + `artifact_type` del `patient_current_state`
 * en parámetros numéricos que los generadores (ECG/Pleth/Resp/Pressure)
 * pueden aplicar sobre la señal fisiológica base.
 *
 * Todos los artefactos SUMAN sobre la señal — nunca reemplazan la
 * fisiología subyacente. Cuando el instructor cambia el estado clínico
 * real, la fisiología cambia; los artefactos son otra capa.
 */

// Deterministic pseudo-random para que los artefactos sean estables entre
// frames sin depender de Math.random (evita jitter visual excesivo).
function hash11(x) {
  const s = Math.sin(x * 12.9898) * 43758.5453;
  return s - Math.floor(s);
}

// Ruido rosa suave (baseline wander)
function baselineWander(t, ampMv) {
  if (!ampMv) return 0;
  return Math.sin(t * 0.0007) * ampMv + Math.sin(t * 0.0013 + 1.7) * ampMv * 0.4;
}

// Ruido blanco filtrado (electrónico)
function whiteNoise(t, ampMv) {
  if (!ampMv) return 0;
  // Suma 3 senos con frecuencias primas para simular ruido de banda ancha
  return (Math.sin(t * 0.117) + Math.sin(t * 0.031 + 2) + Math.sin(t * 0.213 + 4.1)) * ampMv * 0.33;
}

// Interferencia de línea eléctrica (50/60 Hz)
function powerLineHum(t, ampMv, hz = 50) {
  if (!ampMv) return 0;
  return Math.sin((t / 1000) * 2 * Math.PI * hz) * ampMv;
}

// Temblor muscular — banda 20-100 Hz de alta frecuencia y baja amplitud
function muscleTremor(t, ampMv) {
  if (!ampMv) return 0;
  return (
    Math.sin(t * 0.25 + hash11(t | 0) * 6) * ampMv * 0.6 +
    Math.sin(t * 0.42) * ampMv * 0.4
  );
}

// Movimiento — spikes esporádicos de gran amplitud
function movementSpike(t, ampMv) {
  if (!ampMv) return 0;
  const seed = Math.floor(t / 1200); // un evento cada ~1.2s
  const trigger = hash11(seed);
  if (trigger < 0.7) return 0; // 30% de ventanas
  const phase = (t % 1200) / 1200;
  const window = Math.max(0, 1 - Math.abs(phase - hash11(seed + 7)) * 8);
  const sign = hash11(seed + 11) > 0.5 ? 1 : -1;
  return sign * ampMv * window * 2;
}

// Pérdida de contacto de electrodo — señal casi plana con drift lento
function electrodeContact(t, severity) {
  if (!severity) return 0;
  return Math.sin(t * 0.002 + 3) * severity * 8;
}

/**
 * Convierte `signal_quality` (categoría) a un preset de ruido +
 * intensidad. `noise_level` explícito (0..1) sobreescribe la categoría.
 */
export function resolveNoiseProfile(vitals = {}) {
  const explicit = typeof vitals.noise_level === 'number' ? vitals.noise_level : null;
  const quality = vitals.signal_quality || 'good';

  const baseByQuality = {
    excellent: 0.0,
    good: 0.1,
    moderate: 0.3,
    poor: 0.6,
    disconnected: 1.0,
  };
  const base = explicit != null ? Math.min(1, Math.max(0, explicit)) : (baseByQuality[quality] ?? 0.1);

  return {
    quality,
    // niveles (en mV equivalentes del canvas — el motor los escala)
    whiteAmp: base * 1.5,
    baselineAmp: base * 0.8,
    powerHumAmp: quality === 'poor' || quality === 'moderate' ? base * 0.6 : 0,
    contactSeverity: quality === 'disconnected' ? 1 : (quality === 'poor' ? base * 0.4 : 0),
    // multiplicador global de la señal (calidad mala atenúa la fisiología)
    signalAttenuation: quality === 'disconnected' ? 0.05 : (quality === 'poor' ? 0.6 : (quality === 'moderate' ? 0.85 : 1.0)),
  };
}

/**
 * Devuelve el offset (mV en el canvas) generado por artefactos + ruido
 * en el instante `t` para una señal determinada (ECG o Pleth).
 * `signal` = 'ecg' | 'pleth' | 'resp' | 'pressure'
 */
export function sampleArtifact(t, vitals = {}, signal = 'ecg') {
  const profile = resolveNoiseProfile(vitals);
  const artifact = vitals.artifact_type || (profile.quality === 'poor' ? 'muscle_tremor' : null);

  // Escala por tipo de señal (Pleth y Resp son más suaves visualmente)
  const scale = signal === 'ecg' ? 1.0 : signal === 'pleth' ? 0.5 : signal === 'resp' ? 0.35 : 0.7;

  let n = 0;
  n += whiteNoise(t, profile.whiteAmp * scale);
  n += baselineWander(t, profile.baselineAmp * scale);
  n += powerLineHum(t, profile.powerHumAmp * scale);
  n += electrodeContact(t, profile.contactSeverity * scale);

  switch (artifact) {
    case 'movement':
      n += movementSpike(t, 12 * scale);
      break;
    case 'muscle_tremor':
      n += muscleTremor(t, 4 * scale);
      break;
    case 'electrode':
      n += electrodeContact(t, 0.6 * scale);
      break;
    case 'electrical':
      n += powerLineHum(t, 3 * scale, 50);
      break;
    default:
      break;
  }
  return { offset: n, profile };
}

export const SIGNAL_QUALITY_STATES = ['excellent', 'good', 'moderate', 'poor', 'disconnected'];
export const ARTIFACT_TYPES = [null, 'movement', 'muscle_tremor', 'electrode', 'electrical'];
